import { useCallback, useEffect, useRef, useState } from "react";

import {
  parseStoredLoomControlSurfacePoseReceipt,
  serializeLoomControlSurfacePoseReceipt,
} from "./loomControlSurfacePersistence";
import {
  AIRSHOW_CONTROL_DEMO_CASES,
  AIRSHOW_CONTROL_IMPORT_ARCHIVE_MEMBER_PATH,
  AIRSHOW_CONTROL_IMPORT_DEFINITION_REVISION,
  AIRSHOW_CONTROL_IMPORT_RECEIPT,
  AIRSHOW_CONTROL_IMPORT_STORAGE_KEY,
  CESSNA_AIRSHOW_CONTROL_COLLECTION,
  airshowControlDemoCaseForPose,
  airshowControlDemoPose,
  type AirshowControlDemoCase,
} from "./openvspWingControlImportDemo";

const INITIAL_CASE: AirshowControlDemoCase = "port-only";
const INITIAL_POSE = airshowControlDemoPose(INITIAL_CASE);

function projectedDeflection(deflectionDeg: number): number {
  return Math.max(-7, Math.min(7, deflectionDeg * 0.55));
}

function movableBodyPath(side: "port" | "starboard", deflectionDeg: number): string {
  const offset = projectedDeflection(deflectionDeg);
  return side === "port"
    ? `M48 124 L157 115 L157 ${136 + offset} L38 ${146 + offset} Z`
    : `M363 115 L472 124 L482 ${146 + offset} L363 ${136 + offset} Z`;
}

function shortRevision(value: string): string {
  return `${value.slice(0, 18)}…${value.slice(-8)}`;
}

export function AirshowControlImportDemo() {
  const [pose, setPose] = useState(INITIAL_POSE);
  const [demoCase, setDemoCase] = useState<AirshowControlDemoCase>(INITIAL_CASE);
  const [persistence, setPersistence] = useState("Saving revision-bound pose");
  const writeGeneration = useRef(0);
  const state = pose.states[0]!;

  const persist = useCallback((nextPose: typeof pose, success: string) => {
    const generation = writeGeneration.current + 1;
    writeGeneration.current = generation;
    setPersistence("Saving revision-bound pose");
    void serializeLoomControlSurfacePoseReceipt(
      CESSNA_AIRSHOW_CONTROL_COLLECTION,
      nextPose,
    ).then((raw) => {
      if (writeGeneration.current !== generation) return;
      window.localStorage.setItem(AIRSHOW_CONTROL_IMPORT_STORAGE_KEY, raw);
      setPersistence(success);
    }).catch(() => {
      if (writeGeneration.current === generation) setPersistence("Persistence rejected");
    });
  }, []);

  useEffect(() => {
    const raw = window.localStorage.getItem(AIRSHOW_CONTROL_IMPORT_STORAGE_KEY);
    if (!raw) {
      persist(INITIAL_POSE, "Saved revision-bound pose");
      return;
    }
    const restoreGeneration = writeGeneration.current;
    void parseStoredLoomControlSurfacePoseReceipt(
      raw,
      CESSNA_AIRSHOW_CONTROL_COLLECTION,
      AIRSHOW_CONTROL_IMPORT_DEFINITION_REVISION,
    ).then((restored) => {
      if (writeGeneration.current !== restoreGeneration) return;
      const restoredCase = !restored.staleDefinition
        ? airshowControlDemoCaseForPose(restored.pose)
        : null;
      if (!restoredCase) {
        persist(INITIAL_POSE, "Replaced stale pose with current revision");
        return;
      }
      setPose(restored.pose);
      setDemoCase(restoredCase);
      setPersistence("Restored revision-bound pose");
    }).catch(() => {
      if (writeGeneration.current === restoreGeneration) {
        persist(INITIAL_POSE, "Rejected invalid pose and saved current revision");
      }
    });
  }, [persist]);

  const applyCase = (nextCase: AirshowControlDemoCase) => {
    const nextPose = airshowControlDemoPose(nextCase);
    setPose(nextPose);
    setDemoCase(nextCase);
    persist(nextPose, "Saved revision-bound pose");
  };

  return <section
    className="airshow-control-import-demo"
    data-control-mode={state.mode}
    data-demo-case={demoCase}
    data-import-schema={AIRSHOW_CONTROL_IMPORT_RECEIPT.schema}
    data-port-deg={state.portDeflectionDeg}
    data-starboard-deg={state.starboardDeflectionDeg}
    data-testid="airshow-control-import-demo"
  >
    <header>
      <div>
        <span>OpenVSP control import · bounded v1 seam</span>
        <h3>A real Airshow control occurrence, adapted into Loom controls</h3>
        <p>
          The source occurrence stays identifiable while Loom proves the editable target-panel
          definition, topology, kinematics, and pose revisions.
        </p>
      </div>
      <div className="airshow-control-import-revision">
        <strong>{AIRSHOW_CONTROL_IMPORT_RECEIPT.schema}</strong>
        <code title={AIRSHOW_CONTROL_IMPORT_RECEIPT.revision}>
          {shortRevision(AIRSHOW_CONTROL_IMPORT_RECEIPT.revision)}
        </code>
      </div>
    </header>

    <div className="airshow-control-import-grid">
      <section className="airshow-control-import-visual">
        <svg
          aria-label="Schematic Loom target-panel control compatibility preview"
          role="img"
          viewBox="0 0 520 250"
        >
          <path
            className="airshow-control-source-boundary"
            d="M258 36 L35 105 L18 148 L258 126 L502 148 L485 105 Z"
          />
          <path className="airshow-control-fixed" d="M258 50 L55 111 L38 137 L258 116 Z" />
          <path className="airshow-control-fixed" d="M262 50 L465 111 L482 137 L262 116 Z" />
          <path
            className="airshow-control-movable"
            d={movableBodyPath("port", state.portDeflectionDeg)}
          />
          <path
            className="airshow-control-movable"
            d={movableBodyPath("starboard", state.starboardDeflectionDeg)}
          />
          <line className="airshow-control-hinge" x1="48" x2="157" y1="124" y2="115" />
          <line className="airshow-control-hinge" x1="363" x2="472" y1="115" y2="124" />
          <circle className="airshow-control-body" cx="260" cy="98" r="17" />
          <path className="airshow-control-body" d="M253 24 L267 24 L273 166 L247 166 Z" />
          <text className="airshow-control-svg-label" x="37" y="180">PORT {state.portDeflectionDeg > 0 ? "+" : ""}{state.portDeflectionDeg}°</text>
          <text className="airshow-control-svg-label" textAnchor="end" x="483" y="180">STARBOARD {state.starboardDeflectionDeg > 0 ? "+" : ""}{state.starboardDeflectionDeg}°</text>
          <text className="airshow-control-source-label" x="24" y="222">Source occurrence · schematic</text>
          <text className="airshow-control-target-label" textAnchor="end" x="496" y="222">Loom target-panel hinge compatibility</text>
        </svg>
        <div className="airshow-control-import-actions" aria-label="Control displacement examples">
          {AIRSHOW_CONTROL_DEMO_CASES.map((item) => <button
            aria-pressed={demoCase === item.id}
            key={item.id}
            onClick={() => applyCase(item.id)}
            type="button"
          >{item.label}</button>)}
        </div>
      </section>

      <aside className="airshow-control-import-evidence">
        <section>
          <span>Occurrence-preserving map</span>
          <strong>BFXIRXIKXM → vsp-BFXIRXIKXM</strong>
          <code>{AIRSHOW_CONTROL_IMPORT_ARCHIVE_MEMBER_PATH}</code>
          <p>η 0.50–1.00 · adapted target hinge 0.75 c</p>
        </section>
        <section>
          <span>Target-panel compatibility</span>
          <strong>Separate fixed and mirrored movable bodies</strong>
          <p>Loom resolves a straight 3D hinge on the supplied target panels, with rigid semantic TE-down rotation and independent sides.</p>
          <code>{shortRevision(AIRSHOW_CONTROL_IMPORT_DEFINITION_REVISION)}</code>
        </section>
        <section>
          <span>Typed exclusion, never coerced</span>
          <strong>FVFFRUIQIZ · openvsp-control-not-selected</strong>
          <p>Source-frame, group, leading-edge, single-skin, and incompatible target-panel cases fail closed with typed codes.</p>
        </section>
        <section className="airshow-control-import-claim">
          <span>Claim boundary</span>
          <strong>No exact OpenVSP hinge, trim, cove, Boolean, or round-trip claim</strong>
          <p>Schematic only: source hinge fidelity is unproven; the sealed nose and straight hinge are Loom target policies, not reconstructed OpenVSP geometry.</p>
        </section>
        <p className="airshow-control-import-persistence" data-testid="airshow-control-persistence">
          {persistence}
        </p>
      </aside>
    </div>
  </section>;
}
